import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { isLowGpa, LOW_GPA_WARNING_TEXT, LOW_GPA_BLOCK_TEXT } from "../../_util/gpa";
import FileUpload from "../../components/FileUpload";
import DocumentInstructionModal from "../../components/DocumentInstructionModal";
import ButtonMain from "../../components/Button";
import { hideLoader, showLoader } from '../../Store/mainSlice'
import { UploadImagetoFb } from '../../Hooks/uploadImage'
import httpClient from '../../_util/api'
import CustomizedSnackbars from '../../shared/Snackbar/SnackBar'
import { saveAs } from 'file-saver';

const INSTRUCTIONS_PDF_URL =
  "https://firebasestorage.googleapis.com/v0/b/al-kauser-b6277.firebasestorage.app/o/products%2FInstructions%20to%20upload%20documents%20on%20the%20Portal.pdf%2Btyweyey%2B1773097686967-999457?alt=media&token=17afd8de-84a4-4616-bf5d-cc8f23c749f6";
const REFERENCE_LETTER_PDF_URL = 
  "https://firebasestorage.googleapis.com/v0/b/al-kauser-b6277.firebasestorage.app/o/products%2FTemplate%20Reference%20Letter%20for%20Scholarship.pdf%2Btyweyey%2B1774389937719-134219?alt=media&token=3315f7f0-90a3-4727-9a03-9f4250ec9172";


const ApplyForScholarship = () => {
  const dispatch = useDispatch();
  const { token: inviteToken } = useParams();
  const [linkStatus, setLinkStatus] = useState("checking"); // 'checking' | 'valid' | 'invalid'
  const [linkError, setLinkError] = useState("");
  const snackbar_Ref = useRef();
  const transcriptPhotoRef = useRef(null);
  const voucherPhotoRef = useRef(null);
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(true);
  const [universities, setUniversities] = useState([]);
  const facultyOptions = [
    "Architecture & Planning",
    "Business Management",
    "Computing & IT",
    "Education",
    "Engineering & Technology",
    "Fashion, Textile & Media Studies",
    "Health & Medical Sciences",
    "LAW",
    "Life Sciences",
    "Science & Technology",
    "Social Sciences",
    "Misc.",
    "Other",
  ];

  const defaultState = {
    isReturning: true,
    resultOfLastExamFile: { fileName: "", url: "" },
    feeVoucherFile: { fileName: "", url: "" },
    degree: "",
    faculty: "",
    otherFaculty: "",
    enrollmentNo: "",
    grade: "",
    institutionName: "",
    otherInstitutionName: "",
    institutionAddress: "",
    tuttionFee: "",
    classOfLastInstitution: ""
  }
  const [studentData, setStudentData] = useState(defaultState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Low GPA/CGPA gate: { open, mode: 'warn' | 'block', message }
  const [gpaDialog, setGpaDialog] = useState({ open: false, mode: "warn", message: "" });

  const getAllPakistaniUni = async () => {
    try {
      const res = await httpClient.get('students/getAllPakUniversities');
      if (res?.status === 200) {
        const universityNames = res.data.map(item => item.name);
        setUniversities(['Other', ...universityNames]);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
      const msg = error?.response?.data?.message || error?.message || "Failed to load universities.";
      snackbar_Ref.current?.showMessage("error", msg, "", "i-chk-circle");
    }
  };

  useEffect(() => {
    getAllPakistaniUni();
  }, []);

  // This page is only reachable via a one-time application link
  // (/student/apply-scholarship/:token). Validate it before letting the
  // student see or fill out the form.
  useEffect(() => {
    const validateInviteLink = async () => {
      if (!inviteToken) {
        setLinkStatus("invalid");
        setLinkError("This application link is incomplete. Please use the exact link you were sent to apply.");
        return;
      }
      try {
        await httpClient.get(`/students/validateScholarshipInvite/${inviteToken}`);
        setLinkStatus("valid");
      } catch (error) {
        setLinkStatus("invalid");
        setLinkError(error?.response?.data?.message || "This application link is invalid.");
      }
    };
    validateInviteLink();
  }, [inviteToken]);

  const handleFileChange = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      snackbar_Ref.current?.showMessage("error", "Please upload a PDF file.", "", "i-chk-circle");
      return;
    }

    const MAX_FILE_SIZE_MB = 1.5;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      snackbar_Ref.current?.showMessage("error", `File size should not exceed ${MAX_FILE_SIZE_MB} MB.`, "", "i-chk-circle");
      return;
    }

    dispatch(showLoader());

    try {
      const uploadedFile = await UploadImagetoFb(event);
      if (!uploadedFile?.url || !uploadedFile?.status) {
        snackbar_Ref.current?.showMessage("error", "File upload to storage failed. Please check your connection and try again.", "", "i-chk-circle");
        return;
      }
      const newFile = { fileName: file.name, url: uploadedFile.url };
      setStudentData((prev) => ({
        ...prev,
        [field]: newFile,
      }));
    } catch (err) {
      console.error("Upload error:", err);
      const msg = err?.response?.data?.message || err?.message || "File upload failed. Please try again.";
      snackbar_Ref.current?.showMessage("error", msg, "", "i-chk-circle");
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleInputChange = (value, field, type = "text") => {
    setStudentData((prev) => ({
      ...prev,
      [field]: type === "number" ? Number(value) : value
    }));
  };

  const handleAutocompleteValue = (newValue, field) => {
    setStudentData((prev) => ({
      ...prev,
      [field]: newValue || ""
    }));
  };

  const uploadPhotoHandler = (ref) => {
    ref?.current?.click();
  };

  const handleDownloadReferenceLetter = () => {
    const url = REFERENCE_LETTER_PDF_URL.trim();

    if (!url) {
      snackbar_Ref.current?.showMessage(
        "error",
        "Reference letter link is not set.",
        "",
        "i-chk-circle"
      );
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const submitApplication = async (acknowledgedLowGpa = false) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    dispatch(showLoader());
    try {
      const res = await httpClient.post('/students/applyForScholarship', {
        ...studentData,
        inviteToken,
        acknowledgedLowGpa,
      });
      if (res?.status === 200) {
        setStudentData(defaultState);
        snackbar_Ref.current.showMessage("success", res.data.message, "", "i-chk-circle");
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      // Backend also enforces the GPA gate; surface its popup if the client check was skipped.
      if (error?.response?.status === 403 && error?.response?.data?.gpaDecision === "warn") {
        setGpaDialog({ open: true, mode: "warn", message: apiMessage || LOW_GPA_WARNING_TEXT });
      } else if (error?.response?.status === 403 && apiMessage === LOW_GPA_BLOCK_TEXT) {
        setGpaDialog({ open: true, mode: "block", message: apiMessage });
      } else {
        const msg = apiMessage || error?.message || "Failed to submit application. Please try again.";
        snackbar_Ref.current.showMessage("error", msg, "", "i-chk-circle");
      }
    } finally {
      dispatch(hideLoader());
      setIsSubmitting(false);
    }
  };

  const addAttachmentDetailHandler = async () => {
    if (isSubmitting) return;

    if (isLowGpa(studentData.classOfLastInstitution)) {
      dispatch(showLoader());
      try {
        const res = await httpClient.post('/students/gpa/evaluate', {
          gpa: Number(studentData.classOfLastInstitution),
        });
        const decision = res?.data?.decision;
        if (decision === "block") {
          setGpaDialog({ open: true, mode: "block", message: res.data.message || LOW_GPA_BLOCK_TEXT });
          return;
        }
        if (decision === "warn") {
          setGpaDialog({ open: true, mode: "warn", message: res.data.message || LOW_GPA_WARNING_TEXT });
          return;
        }
        // 'ok' / 'override' -> proceed to submit below
      } catch (error) {
        // Evaluate failed — let the backend gate decide on submit.
      } finally {
        dispatch(hideLoader());
      }
    }

    submitApplication(false);
  };
  
  // Gate the form behind the one-time link: nothing renders until it's confirmed valid.
  if (linkStatus !== "valid") {
    return (
      <Paper sx={{ padding: 4, textAlign: "center" }}>
        {linkStatus === "checking" ? (
          <Typography variant="h6">Checking your link…</Typography>
        ) : (
          <>
            <Typography variant="h5" fontWeight="bold" color="error" gutterBottom>
              Link unavailable
            </Typography>
            <Typography variant="body1">{linkError}</Typography>
          </>
        )}
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 2 }}>
       <CustomizedSnackbars ref={snackbar_Ref} />

      <Dialog
        open={gpaDialog.open}
        onClose={() => setGpaDialog((d) => ({ ...d, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: gpaDialog.mode === "block" ? "error.main" : "warning.main", fontWeight: 700 }}>
          {gpaDialog.mode === "block" ? "Application cannot proceed" : "Low GPA/CGPA warning"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {gpaDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {gpaDialog.mode === "warn" ? (
            <>
              <Button onClick={() => setGpaDialog((d) => ({ ...d, open: false }))} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{ backgroundColor: "#4caf50", color: "white" }}
                disabled={isSubmitting}
                onClick={() => {
                  setGpaDialog((d) => ({ ...d, open: false }));
                  submitApplication(true);
                }}
              >
                Continue
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => setGpaDialog((d) => ({ ...d, open: false }))}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DocumentInstructionModal
        pdfUrl={INSTRUCTIONS_PDF_URL}
        open={instructionsModalOpen}
        onClose={() => setInstructionsModalOpen(false)}
        title="Upload document instruction"
        newTabLabel="Open in new tab"
      />
      <h2>APPLY FOR MERITORIOUS SCHOLARSHIP - ATTACHMENTS</h2>
       <Box
        sx={{
          width: '100%',
          boxSizing: 'border-box',
          backgroundColor: '#fff3f3',
          border: '1px solid #ffcccc',
          padding: '16px',
          borderRadius: 2,
          mb: 1,
        }}
      >
        <Typography color="error" fontWeight={600}>
          Please follow the instructions carefully:
        </Typography>
        <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
          <li>
            <Typography>
              <Link
                component="button"
                type="button"
                onClick={handleDownloadReferenceLetter}
                sx={{ color: "error.main", cursor: "pointer", textDecoration: "underline" }}
              >
                Download Template Reference Letter(PDF)
              </Link>
            </Typography>
          </li>
          <li>
            <Typography>
              <Link
                component="button"
                type="button"
                onClick={() => setInstructionsModalOpen(true)}
                sx={{ color: "error.main", cursor: "pointer", textDecoration: "underline" }}
              >
                Upload document instruction
              </Link>
            </Typography>
          </li>
          <li>
            <Typography color="error">
              All fields are required*
            </Typography>
          </li>
          <li>
            <Typography color="error">
              Last exam transcript, including SSC and HSC exams passed                                
            </Typography>
          </li>
          <li>
            <Typography color="error">
              Unpaid Fee Voucher                                
            </Typography>
          </li>
          <li>
            <Typography color="error">
              All documents must be scanned in <strong>PDF format</strong>. Pictures are not accepted.
            </Typography>
          </li>
          <li>
            <Typography color="error">
              Each file must not exceed <strong>1.5 MB</strong> in size.
            </Typography>
          </li>
        </ul>
      </Box>
      <Stack className="grid-2" justifyContent="space-between">
      <TextField
        required
        label="Degree"
        type="text"
        value={studentData.degree}
        onChange={(e) => handleInputChange(e.target.value, "degree", "text")}
      />
      <Autocomplete
        options={facultyOptions}
        value={studentData.faculty || null}
        onChange={(event, newValue) => handleAutocompleteValue(newValue, "faculty")}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Choose Faculty"
            required
            variant="outlined"
          />
        )}
      />
      {studentData.faculty === "Other" && (
        <TextField
          label="Faculty Name"
          value={studentData.otherFaculty}
          onChange={(e) => handleInputChange(e.target.value, "otherFaculty")}
        />
      )}
        <TextField
          required
          label="Semester"
          type="number"
          value={studentData.grade}
          onChange={(e) => handleInputChange(e.target.value, "grade", "number")}
        />
        <TextField
          required
          label="Enrollment #"
          type="text"
          value={studentData.enrollmentNo}
          onChange={(e) => { handleInputChange(e.target.value, "enrollmentNo", "text") }}
        />
        <Autocomplete
          options={universities}
          value={studentData.institutionName || null}
          onChange={(event, newValue) => handleAutocompleteValue(newValue, "institutionName")}
          renderInput={(params) => (
            <TextField {...params} label="Institution Name" required variant="outlined" />
          )}
        />
        {studentData.institutionName === "Other" && (
          <TextField
            label="Institution Name"
            value={studentData.otherInstitutionName}
            onChange={(e) => handleInputChange(e.target.value, "otherInstitutionName")}
          />
        )}
        <TextField
          required
          label="Institution Address"
          value={studentData.institutionAddress}
          onChange={(e) => handleInputChange(e.target.value, "institutionAddress")}
          variant="outlined"
        />
        <TextField
          label="Tuition Fee"
          type="number"
          value={studentData.tuttionFee}
          onChange={(e) => handleInputChange(e.target.value, "tuttionFee", "number")}
        />
        <TextField
          required
          label="GPA/CGPA of previous semester"
          type="number"
          value={studentData.classOfLastInstitution}
          onChange={(e) => handleInputChange(e.target.value, "classOfLastInstitution", "number")}
        />
        <FileUpload
          ref={transcriptPhotoRef}
          file={studentData.resultOfLastExamFile}
          label="Transcript/Result of Last Exam"
          onChange={(e) => handleFileChange(e, "resultOfLastExamFile")}
          onClick={() => uploadPhotoHandler(transcriptPhotoRef)}
          onRemove={() =>
            setStudentData((prev) => ({
              ...prev,
              resultOfLastExamFile: {
                fileName: "",
                url: "",
              },
            }))
          }
        />
        <FileUpload
          ref={voucherPhotoRef}
          file={studentData.feeVoucherFile}
          label="Unpaid Fee Voucher"
          onChange={(e) => handleFileChange(e, "feeVoucherFile")}
          onClick={() => uploadPhotoHandler(voucherPhotoRef)}
          onRemove={() =>
            setStudentData((prev) => ({
              ...prev,
              feeVoucherFile: {
                fileName: "",
                url: "",
              },
            }))
          }
        />
      </Stack>

      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          mt: 2,
          gap: 2
        }}
      >
        <ButtonMain variant="green" onClick={addAttachmentDetailHandler} disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit"}
        </ButtonMain>
      </Stack>
    </Paper>
  );
};

export default ApplyForScholarship;
